import React, {useEffect, useState} from "react"
import {useLazyQuery} from "@apollo/client"
import providerFolderListQuery from "graphql/Storage/Provider/query/providerFolderList"

type FolderLocation = {
  id: string | null
  name: string
}

type FolderPickerProps = {
  providerId: string
  value: {
    id?: string | null
    path: string
  }
  onChange: (value: {id: string | null, path: string}) => void
}

const pathFromStack = (stack: FolderLocation[]) => {
  const path = stack.slice(1).map(folder => folder.name).join("/")
  return path ? `/${path}` : "/"
}

export const FolderPicker = ({providerId, value, onChange}: FolderPickerProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [stack, setStack] = useState<FolderLocation[]>([{id: null, name: "Racine"}])
  const [loadFolders, {data, loading, error}] = useLazyQuery(providerFolderListQuery, {
    fetchPolicy: "network-only",
  })
  const folders = data?.providerFolderList || []

  useEffect(() => {
    setIsOpen(false)
    setStack([{id: null, name: "Racine"}])
  }, [providerId])

  const load = (parentId: string | null) => loadFolders({
    variables: {providerId, parentId},
  })

  const open = () => {
    if (!providerId) return
    const root = [{id: null, name: "Racine"}]
    setStack(root)
    setIsOpen(true)
    load(null)
  }

  const navigateTo = (folder: FolderLocation) => {
    setStack(current => [...current, folder])
    load(folder.id)
  }

  const navigateBreadcrumb = (index: number) => {
    const targetStack = stack.slice(0, index + 1)
    setStack(targetStack)
    load(targetStack[targetStack.length - 1].id)
  }

  const selectCurrentFolder = () => {
    const currentFolder = stack[stack.length - 1]
    onChange({id: currentFolder.id, path: pathFromStack(stack)})
    setIsOpen(false)
  }

  return <div className="folder-picker">
    <div className="input-group">
      <input className="form-control" value={value.path || "/"} readOnly aria-label="Dossier Google Drive sélectionné"/>
      <div className="input-group-append">
        <button type="button" className="btn btn-outline-dark" disabled={!providerId} onClick={open}>
          Parcourir Drive
        </button>
      </div>
    </div>

    {isOpen && <div className="card mt-2 border-dark">
      <div className="card-header py-2 d-flex justify-content-between align-items-center">
        <div aria-label="Fil d’Ariane du dossier">
          {stack.map((folder, index) => <React.Fragment key={`${folder.id || "root"}-${index}`}>
            {index > 0 && <span className="mx-1">/</span>}
            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => navigateBreadcrumb(index)}>
              {folder.name}
            </button>
          </React.Fragment>)}
        </div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setIsOpen(false)}>Fermer</button>
      </div>
      <div className="list-group list-group-flush" style={{maxHeight: 320, overflowY: "auto"}}>
        {loading && <div className="list-group-item text-muted">Chargement des dossiers…</div>}
        {error && <div className="list-group-item text-danger">Impossible de parcourir ce Drive : {error.message}</div>}
        {!loading && !error && folders.length === 0 && <div className="list-group-item text-muted">Ce dossier ne contient aucun sous-dossier.</div>}
        {!loading && folders.map((folder: any) => <button
          type="button"
          className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
          key={folder.id}
          onClick={() => navigateTo({id: folder.id, name: folder.name})}
        >
          <span><span className="mr-2" aria-hidden="true">📁</span>{folder.name}</span>
          <span aria-hidden="true">›</span>
        </button>)}
      </div>
      <div className="card-footer py-2">
        <button type="button" className="btn btn-dark btn-sm" onClick={selectCurrentFolder}>
          Sélectionner « {stack[stack.length - 1].name} »
        </button>
      </div>
    </div>}
  </div>
}
